import {
	AfterViewInit,
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
	OnChanges,
	SimpleChanges,
	ViewChild,
	ViewEncapsulation,
	TemplateRef,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { animate, style, transition, trigger } from '@angular/animations';
import {
	NgbAccordionModule,
	NgbDropdownModule,
	NgbNavModule,
	NgbModal,
	NgbModule,
	NgbTooltip,
} from '@ng-bootstrap/ng-bootstrap';
import {
	Article,
	AudioArticle,
	BackendTag,
	GameData,
	ImageArticle,
	Imagesizes,
	PluginType,
	RendererGameDefinitionService,
	VideoArticle,
} from '@meta-press/shared-plugins';
import { SourceMaterialService } from '../source-material-service/source-material.service';
import { LoginService } from '../login-service/login.service';
import { DataService } from '../services/data.service';
import { ExportService } from '../export-service/export.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NotificationService } from '../notification-service/notification.service';
import { PDFImageExtractorComponent } from '../pdf-image-extractor/pdf-image-extractor.component';
import { FileUploadModalComponent } from '../file-upload-modal/file-upload-modal.component';
import {
	ImagePreviewModalComponent,
	ImagePreviewData,
} from '../image-preview-modal/image-preview-modal.component';
import { forkJoin } from 'rxjs';

/** This component is used as a global media browser for selecting documents and images in any plugin. */
@Component({
	selector: 'app-article-search-list',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		DatePipe,
		NgbNavModule,
		NgbDropdownModule,
		NgbAccordionModule,
		NgbModule,
		NgbTooltip,
	],
	templateUrl: './article-search-list.component.html',
	styleUrls: [
		'./article-search-list.component.scss',
		'../../../../../libs/shared/styles/src/styles.scss',
	],
	animations: [
		trigger('fadeInImmediately', [
			transition(':enter', [
				style({ opacity: 0 }),
				animate('0.35s', style({ opacity: 1 })),
			]),
		]),
	],
	encapsulation: ViewEncapsulation.ShadowDom,
})
export class ArticleSearchListComponent
	implements OnInit, AfterViewInit, OnChanges
{
	@Input() sourceArticles: Article[] = [];
	@Input() selectedArticles: Article[] = [];
	@Input() searchText = '';
	@Input() owner: 'me' | 'other' | 'any' = 'any';
	@Input() tableTitle = '';
	@Input() showPreviewButton = true;
	@Input() showTagButton = true;
	@Input() showGenerateButton = true;
	@Input() showDeleteButton = true;
	@Input() nestChildren = true; // If true (default), child articles are nested under parents. If false, children shown at same level as parents.
	@Input() selectOnStart: Article[] = []; // Articles to automatically select when component initializes
	@Input() showFileUpload = false; // If true, shows "Lataa kuvia..." button in top navigation bar
	@Input() sourceArticlesGetter?: () => Article[]; // Optional callback to get fresh articles after file upload

	@Output() searchTextChange = new EventEmitter<string>();
	@Output() editArticle = new EventEmitter<Article>();
	@Output() exportArticle = new EventEmitter<Article>();
	@Output() selectArticles = new EventEmitter<{
		articles: Article[];
		removePrevious: boolean;
	}>();
	@Output() selectedArticlesChange = new EventEmitter<Article[]>();
	@Output() startExport = new EventEmitter<void>();
	@Output() toggleTag = new EventEmitter<string>();
	@Output() selectAll = new EventEmitter<Article[]>();
	@Output() addTagsToSelected = new EventEmitter<Article[]>();
	@Output() manageTags = new EventEmitter<void>();
	@Output() openFileSettings = new EventEmitter<Article>();

	@ViewChild('deleteConfirmModal') deleteConfirmModal!: TemplateRef<any>;

	Imagesizes: typeof Imagesizes = Imagesizes;

	filteredArticles: Article[] = [];

	private cachedArticles: Article[] = []; // Store the original articles for filtering

	// Local selection cache
	private localSelectedArticles: Article[] = [];
	private originalSelection: Article[] = []; // Store original selection for slave mode

	// Display mode: list (default), preview, grouped, folders, hierarchy
	displayMode: 'list' | 'preview' | 'grouped' | 'folders' | 'hierarchy' =
		'grouped';

	// Media type filters - all selected by default
	showImageMedia = true;
	showFileMedia = true;
	showVideoMedia = true;
	showAudioMedia = true;
	showFolders = true;

	public IsSlave = false; // If true, the selector
	public selectionCap = 0; // If 0, the selector is limited to this number of articles

	deletingArticleIds: number[] = []; // Track articles being deleted
	loadingArticleIds: Set<number> = new Set(); // Track articles being loaded for preview
	pdfExtractingArticleIds: Set<number> = new Set(); // Track articles being extracted for PDF images

	// Sorting properties
	sortColumn: 'title' | 'dateCreated' | null = 'dateCreated';
	sortDirection: 'asc' | 'desc' = 'desc';

	// Navigation history for display mode and search
	displayHistory: Array<{
		displayMode: 'list' | 'preview' | 'grouped' | 'folders' | 'hierarchy';
		searchText: string;
	}> = [];

	// Hierarchy mode state - current folder path (empty array = root)
	currentFolderPath: number[] = [];

	// State for tracking expanded parent articles (showing their children)
	expandedParentIds: Set<number> = new Set();

	// State for tracking child article view mode per parent (thumbnails or list)
	childViewModes: Map<number, 'list' | 'thumbnails'> = new Map();

	// State for tracking image view mode (thumbnails or list)
	imageViewMode: 'list' | 'thumbnails' = 'thumbnails';

	constructor(
		public sourceMaterialService: SourceMaterialService,
		public loginService: LoginService,
		private modalService: NgbModal,
		private exportService: ExportService,
		private rendererGameDefinitionService: RendererGameDefinitionService,
		private notificationService: NotificationService,
	) {}

	get shouldShowActionsColumn(): boolean {
		return (
			this.showPreviewButton ||
			this.showTagButton ||
			this.showGenerateButton ||
			this.showDeleteButton
		);
	}

	ngOnInit(): void {
		// Don't use input articles directly - we process local copy instead
		this.cachedArticles = this.sourceArticles;
		// Initialize local selection from input
		this.localSelectedArticles = [...this.selectedArticles];

		// Auto-select articles from selectOnStart if provided
		if (this.selectOnStart && this.selectOnStart.length > 0) {
			// Merge selectOnStart with existing selectedArticles, avoiding duplicates
			this.selectOnStart.forEach((article) => {
				if (
					!this.localSelectedArticles.some(
						(selected) => selected.id === article.id,
					)
				) {
					this.localSelectedArticles.push(article);
				}
			});
		}

		// Store original selection for slave mode
		if (this.IsSlave) {
			this.originalSelection = [...this.selectedArticles];
		}
		// Initialize filtered articles to ensure they're available
		this.filteredArticles = this.searchArticles();
	}

	ngAfterViewInit(): void {
		// // Re-run search after view init to make sure we have the most up-to-date data
		// // This is crucial for when the component is loaded in a dynamic modal
		// setTimeout(() => {
		// 	this.filteredArticles = this.searchArticles();
		// }, 0);
	}

	GetDocumentArticles(sourceArticles: Article[]): Article[] {
		return this.getArticlesByTypes(['pdf', 'doc', 'text'], sourceArticles);
	}

	GetAudioArticles(sourceArticles: Article[]): Article[] {
		const articles = this.getArticlesByTypes(['audio'], sourceArticles);
		// Only filter out child articles if nestChildren is true (default behavior)
		return this.nestChildren
			? articles.filter(
					(article) =>
						article.parentArticleId === undefined ||
						article.parentArticleId === -1,
				)
			: articles;
	}

	GetVideoArticles(sourceArticles: Article[]): Article[] {
		const articles = this.getArticlesByTypes(['video'], sourceArticles);
		// Only filter out child articles if nestChildren is true (default behavior)
		return this.nestChildren
			? articles.filter(
					(article) =>
						article.parentArticleId === undefined ||
						article.parentArticleId === -1,
				)
			: articles;
	}

	GetImageArticles(sourceArticles: Article[]): Article[] {
		const articles = this.getArticlesByTypes(['image'], sourceArticles);
		// Only filter out child articles if nestChildren is true (default behavior)
		return this.nestChildren
			? articles.filter(
					(article) =>
						article.parentArticleId === undefined ||
						article.parentArticleId === -1,
				)
			: articles;
	}

	CastArticleToImage(article: Article): ImageArticle | undefined {
		if (article.type === 'image') {
			return article as ImageArticle;
		}
		return undefined;
	}

	CastArticleToPDF(article: Article): Article | undefined {
		if (article.type === 'pdf') {
			return article;
		}
		return undefined;
	}

	CastArticleToVideo(article: Article): Article | undefined {
		if (article.type === 'video') {
			return article as VideoArticle;
		}
		return undefined;
	}

	CastArticleToAudio(article: Article): Article | undefined {
		if (article.type === 'audio') {
			return article as AudioArticle;
		}
		return undefined;
	}

	ngOnChanges(changes: SimpleChanges): void {
		// console.log('ArticleSearchListComponent ngOnChanges', changes);

		// Update local selection if selectedArticles input changes
		if (
			changes['selectedArticles'] &&
			!changes['selectedArticles'].firstChange
		) {
			this.localSelectedArticles = [...this.selectedArticles];
		}

		// Update cached articles if sourceArticles changes
		if (changes['sourceArticles']) {
			this.cachedArticles = this.sourceArticles;
		}

		this.filteredArticles = this.searchArticles();
	}

	updateSearch(value: string) {
		// Save to history before changing
		if (value !== this.searchText) {
			this.pushToHistory();
		}
		this.searchText = value;
		this.searchTextChange.emit(value);
		this.filteredArticles = this.searchArticles();
	}

	updateFilters() {
		// Update filtered articles when media type filters change
		this.filteredArticles = this.searchArticles();
	}

	getInactiveFilterCount(): number {
		let count = 0;
		if (!this.showFileMedia) count++;
		if (!this.showImageMedia) count++;
		if (!this.showAudioMedia) count++;
		if (!this.showFolders) count++;
		return count;
	}

	onToggleTag(tag: string): void {
		this.toggleTag.emit(tag);
		this.filteredArticles = this.searchArticles();
	}

	onSelectArticles(articles: Article[], removePrevious: boolean): void {
		// Track which articles to emit to parent component
		let articlesToEmit = articles;

		// Update local selection cache
		if (removePrevious) {
			// When replacing selection entirely, expand to include children
			const expandedArticles = this.expandArticlesWithChildren(articles);
			this.localSelectedArticles = [...expandedArticles];
			// Emit expanded articles so parent component gets both parent and children
			articlesToEmit = expandedArticles;
		} else {
			// Handle toggle selection with parent-child logic
			articles.forEach((article) => {
				const isCurrentlySelected =
					this.localSelectedArticles.findIndex(
						(selected) => selected.id === article.id,
					) !== -1;

				if (isCurrentlySelected) {
					// Article is selected, deselect it and its children
					this.deselectArticleAndChildren(article);
				} else {
					// Article not selected, select it and its children
					this.selectArticleAndChildren(article);
				}
			});
		}

		// Emit changes for parent to handle (emit expanded articles when removePrevious is true)
		this.selectArticles.emit({ articles: articlesToEmit, removePrevious });

		// Emit updated selection if not in slave mode (immediate sync)
		if (!this.IsSlave) {
			this.selectedArticlesChange.emit([...this.localSelectedArticles]);
		}
	}

	onEditArticle(article: any): void {
		switch (article.type) {
			case 'image':
				this.openImageModal(article);
				break;
			case 'pdf':
			case 'video':
			case 'audio':
			case 'doc':
			case 'text':
			default:
				// Add article to loading set
				this.loadingArticleIds.add(article.id);
				// Use GetURLAsObject and open in new tab for universal file preview
				this.sourceMaterialService
					.GetURLAsObject(article.id)
					.subscribe({
						next: (url) => {
							// Remove from loading set
							this.loadingArticleIds.delete(article.id);
							if (url) {
								window.open(url, '_blank');
							} else {
								console.error(
									'Could not generate URL for article:',
									article,
								);
							}
						},
						error: (error) => {
							// Remove from loading set on error
							this.loadingArticleIds.delete(article.id);
							console.error(
								'Error getting file URL for article:',
								article,
								error,
							);
						},
					});
				break;
		}
	}

	onExportArticle(article: Article): void {
		this.exportArticle.emit(article);
	}

	onStartExport(): void {
		this.startExport.emit();
	}

	onSelectAll(articles: Article[]): void {
		// Expand articles to include child articles
		const expandedArticles = this.expandArticlesWithChildren(articles);

		// Check if all expanded articles are currently selected
		const allSelected = expandedArticles.every((article) =>
			this.localSelectedArticles.some(
				(selected) => selected.id === article.id,
			),
		);

		if (allSelected) {
			// Deselect all articles (including children)
			this.localSelectedArticles = this.localSelectedArticles.filter(
				(selected) =>
					!expandedArticles.some(
						(article) => article.id === selected.id,
					),
			);
		} else {
			// Select all articles (including children, avoiding duplicates)
			expandedArticles.forEach((article) => {
				if (
					!this.localSelectedArticles.some(
						(selected) => selected.id === article.id,
					)
				) {
					this.localSelectedArticles.push(article);
				}
			});
		}

		// Emit to parent to handle the actual selection logic (emit original articles, not expanded)
		this.selectAll.emit(articles);

		// Emit updated selection if not in slave mode (immediate sync)
		if (!this.IsSlave) {
			this.selectedArticlesChange.emit([...this.localSelectedArticles]);
		}
	}

	getSearchFieldTags(searchString: string): string[] {
		return searchString
			.split(' ')
			.filter((t) => t.startsWith('#') && t.length > 1)
			.map((t) => t.substring(1))
			.map((t) => t.toLowerCase());
	}

	IsArticleSelected(article: Article): boolean {
		return this.localSelectedArticles.some(
			(selected) => selected.id === article.id,
		);
	}

	isTagSelected(tag: string): boolean {
		const tags: string[] = this.searchText
			.split(' ')
			.map((t) => t.toLowerCase());
		return tags.some((t) => t == '#' + tag.toLowerCase());
	}

	// Get articles by specific types - used for grouped view
	getArticlesByTypes(types: string[], sourceArticles: Article[]): Article[] {
		return sourceArticles.filter((article) => types.includes(article.type));
	}

	// Get the appropriate image of an article when it's an ImageArticle
	getImageSrc(article: Article): string | undefined {
		if (article.type === 'image') {
			const imageArticle = article as ImageArticle;
			return imageArticle.sourceType === 'local'
				? imageArticle.localPath
				: imageArticle.url;
		}
		return undefined;
	}

	private searchArticles(): Article[] {
		// In hierarchy mode, return articles at current path instead of search results
		if (this.displayMode === 'hierarchy') {
			return this.getArticlesAtCurrentPath();
		}

		// Normal search logic for other modes
		const tagsInSearchField: string[] = this.getSearchFieldTags(
			this.searchText,
		);

		const tagsIdsInSearchField: number[] =
			this.sourceMaterialService.GetTagIdsByNames(tagsInSearchField);

		const searchWordsInSearchField: string[] = this.searchText
			.split(' ')
			.filter((word) => !word.startsWith('#'));

		// First filter by type based on media filters
		const filteredByType = this.cachedArticles.filter((article) => {
			// Only skip child articles in initial filtering if nestChildren is true
			if (
				this.nestChildren &&
				article.parentArticleId !== undefined &&
				article.parentArticleId > -1
			) {
				return false;
			}

			if (article.type === 'image') return this.showImageMedia;
			if (article.type === 'pdf' || article.type === 'doc')
				return this.showFileMedia;
			if (article.type === 'video') return this.showVideoMedia;
			if (article.type === 'audio') return this.showAudioMedia;
			if (article.type === 'text') return this.showFileMedia; // Treat text as document files
			return false;
		});

		// Then apply the rest of the filters
		const filtered = filteredByType.filter((article) => {
			const isOwnerMatch =
				this.owner == 'any' ||
				(this.owner == 'me' && article.isMine) ||
				(this.owner == 'other' && !article.isMine);

			const isTitleMatch = searchWordsInSearchField.every(
				(word) =>
					article.title.toLowerCase().indexOf(word.toLowerCase()) >
					-1,
			);

			const isContentMatch = searchWordsInSearchField.every(
				(word) =>
					article.content.toLowerCase().indexOf(word.toLowerCase()) >
					-1,
			);

			const hasTagMatch = this.checkTagMatch(
				tagsIdsInSearchField,
				article.tags,
			);

			return (
				isOwnerMatch &&
				(isTitleMatch ||
					isContentMatch ||
					searchWordsInSearchField.length == 0) &&
				hasTagMatch
			);
		});

		// Add all child articles for any included parent articles (only if nestChildren is true)
		// Child articles bypass all filtering criteria - they're included purely based on parent inclusion
		let allArticles: Article[];
		if (this.nestChildren) {
			const parentIds = new Set(filtered.map((article) => article.id));
			const childArticles = this.cachedArticles.filter(
				(article) =>
					article.parentArticleId !== undefined &&
					article.parentArticleId > -1 &&
					parentIds.has(article.parentArticleId),
			);
			allArticles = [...filtered, ...childArticles];
		} else {
			// When nestChildren is false, child articles are already included in filtered results
			allArticles = filtered;
		}

		// Apply sorting to the filtered results
		return this.applySorting(allArticles);
	}

	openImageModal(imageArticle: any): void {
		const modalRef = this.modalService.open(ImagePreviewModalComponent, {
			size: 'lg',
			centered: true,
			scrollable: true,
		});

		// Get image URL asynchronously
		this.sourceMaterialService.GetURLAsObject(imageArticle.id).subscribe({
			next: (imageUrl) => {
				if (imageUrl) {
					// Prepare image data for the modal
					const imageData: ImagePreviewData = {
						imageUrl: imageUrl,
						title: imageArticle.title,
						subtitle: imageArticle.creator,
						altText: imageArticle.title,
					};

					// Set the image data in the modal component
					modalRef.componentInstance.setImageData(imageData);
				}
			},
			error: (error) => {
				console.error('Error getting image URL:', error);
			},
		});

		// If you also want to mark the image as selected when viewing
		this.onSelectArticles([imageArticle], true);
	}

	/**
	 * Count child articles for a given parent article (excluding orphaned children)
	 */
	getChildArticleCount(parentId: number): number {
		return this.cachedArticles.filter(
			(article) =>
				article.parentArticleId !== undefined &&
				article.parentArticleId === parentId &&
				!this.isOrphanedChildArticle(article),
		).length;
	}

	/**
	 * Check if an article is an orphaned child article (has parentArticleId but parent doesn't exist)
	 */
	isOrphanedChildArticle(article: Article): boolean {
		// If article has no parent or is a root article, it's not orphaned
		if (
			article.parentArticleId === undefined ||
			article.parentArticleId === -1
		) {
			return false;
		}

		// Check if the parent article exists in the cached articles
		const parentExists = this.cachedArticles.some(
			(a) => a.id === article.parentArticleId,
		);

		// If parent doesn't exist, this is an orphaned child
		return !parentExists;
	}

	/**
	 * Get child articles for a given parent article
	 */
	getChildArticles(parentId: number): Article[] {
		return this.cachedArticles.filter(
			(article) => article.parentArticleId === parentId,
		);
	}

	/**
	 * Check if a parent article is expanded (showing children)
	 */
	isParentExpanded(parentId: number): boolean {
		return this.expandedParentIds.has(parentId);
	}

	/**
	 * Toggle the expansion state of a parent article
	 */
	toggleParentExpansion(parentId: number): void {
		if (this.expandedParentIds.has(parentId)) {
			this.expandedParentIds.delete(parentId);
		} else {
			this.expandedParentIds.add(parentId);
		}
		// No need to refresh filteredArticles - child visibility is handled by template logic
	}

	/**
	 * Get the view mode for child articles of a parent (defaults to 'thumbnails')
	 */
	getChildViewMode(parentId: number): 'list' | 'thumbnails' {
		return this.childViewModes.get(parentId) || 'thumbnails';
	}

	/**
	 * Toggle the view mode for child articles between list and thumbnails
	 */
	toggleChildViewMode(parentId: number): void {
		const currentMode = this.getChildViewMode(parentId);
		const newMode = currentMode === 'list' ? 'thumbnails' : 'list';
		this.childViewModes.set(parentId, newMode);
	}

	/**
	 * Expand article selection to include child articles when parent is selected
	 */
	private expandArticlesWithChildren(articles: Article[]): Article[] {
		const expandedArticles: Article[] = [];
		const articleIds = new Set<number>();

		articles.forEach((article) => {
			// Add the original article if not already added
			if (!articleIds.has(article.id)) {
				expandedArticles.push(article);
				articleIds.add(article.id);
			}

			// If this article is a parent (has children), add its children too
			const childArticles = this.getChildArticles(article.id);
			childArticles.forEach((child) => {
				if (!articleIds.has(child.id)) {
					expandedArticles.push(child);
					articleIds.add(child.id);
				}
			});
		});

		return expandedArticles;
	}

	/**
	 * Select an article and all its child articles
	 */
	private selectArticleAndChildren(article: Article): void {
		// Select the parent article
		if (
			this.selectionCap === 0 ||
			this.localSelectedArticles.length < this.selectionCap
		) {
			const index = this.localSelectedArticles.findIndex(
				(selected) => selected.id === article.id,
			);
			if (index === -1) {
				this.localSelectedArticles.push(article);
			}
		}

		// Select all child articles
		const childArticles = this.getChildArticles(article.id);
		childArticles.forEach((child) => {
			if (
				this.selectionCap === 0 ||
				this.localSelectedArticles.length < this.selectionCap
			) {
				const childIndex = this.localSelectedArticles.findIndex(
					(selected) => selected.id === child.id,
				);
				if (childIndex === -1) {
					this.localSelectedArticles.push(child);
				}
			}
		});
	}

	/**
	 * Deselect an article and all its child articles
	 */
	private deselectArticleAndChildren(article: Article): void {
		// Deselect the parent article
		const index = this.localSelectedArticles.findIndex(
			(selected) => selected.id === article.id,
		);
		if (index !== -1) {
			this.localSelectedArticles.splice(index, 1);
		}

		// Deselect all child articles
		const childArticles = this.getChildArticles(article.id);
		childArticles.forEach((child) => {
			const childIndex = this.localSelectedArticles.findIndex(
				(selected) => selected.id === child.id,
			);
			if (childIndex !== -1) {
				this.localSelectedArticles.splice(childIndex, 1);
			}
		});
	}

	DeleteArticles(articles: Article[]): void {
		articles.forEach((article) => {
			this.DeleteArticle(article);
		});
	}

	/**
	 * Opens a confirmation modal before deleting an article
	 * @param article Article to delete
	 * @param modalTemplate Reference to the modal template
	 */
	confirmDeleteArticle(article: Article, modalTemplate: any): void {
		const modalRef = this.modalService.open(modalTemplate, {
			centered: true,
		});

		modalRef.result
			.then((result) => {
				if (result === 'confirm') {
					this.DeleteArticle(article);
				}
			})
			.catch(() => {
				// Modal was dismissed, do nothing
			});
	}

	async DeleteArticle(article: Article): Promise<void> {
		// Find all child articles that will also be deleted
		const childArticles = this.getChildArticles(article.id);

		// Add article IDs to deleting list immediately to show spinner
		const allArticleIds = [
			article.id,
			...childArticles.map((child) => child.id),
		];
		this.deletingArticleIds.push(...allArticleIds);

		// Check if article or any of its children are used in exports before deletion
		const allArticlesToDelete = [article, ...childArticles];
		const exportsUsingArticles = new Set<string>();

		// Check exports for parent and all children
		for (const articleToCheck of allArticlesToDelete) {
			const exports = await this.getExportsUsingArticle(
				articleToCheck.id,
			);
			exports.forEach((exportName) =>
				exportsUsingArticles.add(exportName),
			);
		}

		if (exportsUsingArticles.size > 0) {
			// Remove from deleting list since user needs to confirm
			this.deletingArticleIds = this.deletingArticleIds.filter(
				(id) => !allArticleIds.includes(id),
			);
			// Show modal warning about exports using this article and its children
			this.showExportConflictModal(
				article,
				Array.from(exportsUsingArticles),
				childArticles.length,
			);
			return;
		}

		// Proceed with deletion if no conflicts
		// Note: performArticleDeletion will add IDs again, so remove them first to avoid duplicates
		this.deletingArticleIds = this.deletingArticleIds.filter(
			(id) => !allArticleIds.includes(id),
		);
		this.performArticleDeletion(article, childArticles);
	}

	/**
	 * Checks which exports contain the given article ID
	 */
	private async getExportsUsingArticle(articleId: number): Promise<string[]> {
		const exportNames: string[] = [];

		for (const exportItem of this.exportService.Exports) {
			if (exportItem.gameData) {
				// Get the game definition to determine the correct GameData type
				const gameDefinition =
					await this.rendererGameDefinitionService.getGameDefinition(
						exportItem.gameData.type,
					);

				if (gameDefinition) {
					// First add methods to the main GameData object
					GameData.addMethods(
						exportItem.gameData,
						gameDefinition.gameDataType,
					);

					// Build the GameData types map by loading constructors for all available types
					const availableTypes =
						this.rendererGameDefinitionService.AvailablePluginTypes;
					const gameDataTypesMap = new Map<
						PluginType,
						new () => GameData
					>();

					for (const pluginType of availableTypes) {
						try {
							const constructor =
								await this.rendererGameDefinitionService.getGameDataConstructor(
									pluginType,
								);
							if (constructor) {
								gameDataTypesMap.set(pluginType, constructor);
							}
						} catch (error) {
							console.error(
								`Failed to load constructor for type ${pluginType}:`,
								error,
							);
						}
					}

					// Then add prototypes to all nested GameData objects
					exportItem.gameData.addPrototypesToAllGameData(
						gameDataTypesMap,
					);

					// Get all article IDs used in this export's GameData
					const usedArticleIds =
						exportItem.gameData.GetUsedArticles();
					if (usedArticleIds.includes(articleId)) {
						exportNames.push(exportItem.name);
					}
				}
			}
		}

		return exportNames;
	}

	/**
	 * Shows modal warning user about exports that use the article
	 */
	private showExportConflictModal(
		article: Article,
		exportNames: string[],
		childArticleCount: number = 0,
	): void {
		const modalRef = this.modalService.open(ExportConflictModalComponent, {
			centered: true,
			backdrop: 'static',
		});

		modalRef.componentInstance.exportNames = exportNames;
		modalRef.componentInstance.articleTitle = article.title;
		modalRef.componentInstance.childArticleCount = childArticleCount;

		modalRef.result
			.then((confirmed: boolean) => {
				if (confirmed) {
					// Get child articles again for deletion
					const childArticles = this.getChildArticles(article.id);
					this.performArticleDeletion(article, childArticles);
				}
			})
			.catch(() => {
				// Modal was dismissed, do nothing
			});
	}

	/**
	 * Performs the actual article deletion including cascade deletion of child articles
	 */
	private performArticleDeletion(
		article: Article,
		childArticles: Article[] = [],
	): void {
		// Add all article IDs (parent and children) to deleting list
		const allArticleIds = [
			article.id,
			...childArticles.map((child) => child.id),
		];
		const allIds = new Set(allArticleIds);
		this.deletingArticleIds.push(...allArticleIds);

		// Delete child articles first, then parent article
		const deleteObservables = [
			...childArticles.map((child) =>
				this.sourceMaterialService.DeleteArticle(child),
			),
			this.sourceMaterialService.DeleteArticle(article),
		];

		// If there are no child articles, just delete the parent
		if (deleteObservables.length === 1) {
			deleteObservables[0].subscribe({
				next: () => {
					// Remove from local caches
					this.cachedArticles = this.cachedArticles.filter(
						(a) => a.id !== article.id,
					);
					this.filteredArticles = this.filteredArticles.filter(
						(a) => a.id !== article.id,
					);
					// Remove from deleting list
					this.deletingArticleIds = this.deletingArticleIds.filter(
						(id) => id !== article.id,
					);
				},
				error: (error: any) => {
					console.error('Error deleting article:', error);
					this.notificationService.ShowToast(
						'Tiedoston poisto epäonnistui: ' + error.message,
						'danger',
					);
					// Remove from deleting list
					this.deletingArticleIds = this.deletingArticleIds.filter(
						(id) => id !== article.id,
					);
				},
			});
		} else {
			// Use forkJoin to delete all articles in parallel
			forkJoin(deleteObservables).subscribe({
				next: () => {
					// Remove all articles (parent and children) from local caches
					this.cachedArticles = this.cachedArticles.filter(
						(a) => !allIds.has(a.id),
					);
					this.filteredArticles = this.filteredArticles.filter(
						(a) => !allIds.has(a.id),
					);
					// Remove all from deleting list
					this.deletingArticleIds = this.deletingArticleIds.filter(
						(id) => !allIds.has(id),
					);

					// Show success notification
					const message =
						childArticles.length > 0
							? `Tiedosto ja ${childArticles.length} alitiedostoa poistettu`
							: 'Tiedosto poistettu';
					this.notificationService.ShowToast(message, 'success');
				},
				error: (error: any) => {
					console.error('Error deleting articles:', error);
					this.notificationService.ShowToast(
						'Tiedostojen poisto epäonnistui: ' + error.message,
						'danger',
					);
					// Remove all from deleting list
					this.deletingArticleIds = this.deletingArticleIds.filter(
						(id) => !allIds.has(id),
					);
				},
			});
		}
	}

	isArticleBeingDeleted(article: Article): boolean {
		return this.deletingArticleIds.includes(article.id);
	}

	/**
	 * Checks if an article or any of its children are being deleted
	 */
	isArticleOrChildrenBeingDeleted(article: Article): boolean {
		// Check if the article itself is being deleted
		if (this.deletingArticleIds.includes(article.id)) {
			return true;
		}
		// Check if any child articles are being deleted
		const childArticles = this.getChildArticles(article.id);
		return childArticles.some((child) =>
			this.deletingArticleIds.includes(child.id),
		);
	}

	isArticleBeingLoaded(article: Article): boolean {
		return this.loadingArticleIds.has(article.id);
	}

	isPDFBeingExtracted(article: Article): boolean {
		return this.pdfExtractingArticleIds.has(article.id);
	}

	/**
	 * Returns the currently selected articles - useful for dynamic modal integration
	 */
	getData(): Article[] {
		// In slave mode, confirming selection should trigger the emission
		if (this.IsSlave) {
			return this.confirmSelection();
		}
		return [...this.localSelectedArticles];
	}

	/**
	 * For slave mode: confirm selection and emit final result
	 */
	confirmSelection(): Article[] {
		const finalSelection = [...this.localSelectedArticles];
		this.selectedArticlesChange.emit(finalSelection);
		return finalSelection;
	}

	/**
	 * For slave mode: cancel selection and restore original
	 */
	cancelSelection(): void {
		this.localSelectedArticles = [...this.originalSelection];
	}

	AddTagsToSelectedArticles(): void {
		// Emit an event to parent component to handle tag addition
		if (this.localSelectedArticles.length > 0) {
			this.addTagsToSelected.emit([...this.localSelectedArticles]);
		}
	}

	onManageTags(): void {
		// Emit an event to parent component to open tag management modal
		this.manageTags.emit();
	}

	/**
	 * Opens the file upload modal for uploading new images/files
	 */
	openFileUploadModal(): void {
		const uploadModalRef = this.modalService.open(
			FileUploadModalComponent,
			{
				size: 'xl',
				centered: true,
				backdrop: 'static',
			},
		);

		// Handle modal result
		uploadModalRef.result
			.then(() => {
				// Files were uploaded successfully
				console.log(
					'Files uploaded successfully, refreshing article list...',
				);

				// If getter is provided, fetch fresh articles and update
				if (this.sourceArticlesGetter) {
					const updatedArticles = this.sourceArticlesGetter();
					this.sourceArticles = updatedArticles;
					this.cachedArticles = updatedArticles;
					this.filteredArticles = this.searchArticles();
				}
			})
			.catch(() => {
				// Modal was dismissed
				console.log('File upload modal dismissed');
			});
	}

	onOpenFileSettings(article: Article): void {
		// Emit an event to parent component to open file settings modal
		this.openFileSettings.emit(article);
	}

	/**
	 * Open PDF image extractor modal for PDF articles
	 * Downloads the PDF file first, then opens the extractor
	 */
	onExtractPDFImages(article: Article): void {
		if (article.type !== 'pdf') {
			return;
		}

		// Add article to PDF extraction set
		this.pdfExtractingArticleIds.add(article.id);

		// Download the PDF file first
		this.sourceMaterialService.GetURLAsObject(article.id).subscribe({
			next: async (url) => {
				this.pdfExtractingArticleIds.delete(article.id);

				if (!url) {
					this.notificationService.ShowToast(
						'PDF-tiedoston lataaminen epäonnistui',
						'danger',
					);
					return;
				}

				try {
					// Fetch the PDF file as blob
					const response = await fetch(url);
					if (!response.ok) {
						throw new Error(
							`HTTP error! status: ${response.status}`,
						);
					}

					const blob = await response.blob();
					const file = new File([blob], article.title, {
						type: 'application/pdf',
					});

					// Open PDF image extractor modal
					const modalRef = this.modalService.open(
						PDFImageExtractorComponent,
						{
							size: 'xl',
							centered: true,
							backdrop: 'static',
						},
					);

					// Set the PDF file for extraction
					modalRef.componentInstance.setPdfFile(file);

					// Handle modal result
					modalRef.result
						.then((result) => {
							if (result?.success && result?.imageBlobs) {
								console.log(
									`PDF extraction completed: ${result.imageCount} images selected`,
								);

								// Open file upload modal with the extracted images
								this.openFileUploadWithImages(
									result.imageBlobs,
									article.title,
									article.id,
								);
							}
						})
						.catch(() => {
							// Modal was dismissed, do nothing
						});
				} catch (error) {
					console.error('Error fetching PDF file:', error);
					this.notificationService.ShowToast(
						'PDF-tiedoston lataaminen epäonnistui: ' +
							(error as Error).message,
						'danger',
					);
				}
			},
			error: (error) => {
				this.pdfExtractingArticleIds.delete(article.id);
				console.error('Error getting PDF URL:', error);
				this.notificationService.ShowToast(
					'PDF-tiedoston URL:n hakeminen epäonnistui: ' +
						error.message,
					'danger',
				);
			},
		});
	}

	/**
	 * Open file upload modal with extracted images from PDF
	 * @param imageBlobs Array of image blobs to upload
	 * @param sourceFileName Original PDF filename for naming
	 * @param parentArticleId ID of the parent article (source PDF)
	 */
	private openFileUploadWithImages(
		imageBlobs: Blob[],
		sourceFileName: string,
		parentArticleId: number,
	): void {
		// Open file upload modal
		const uploadModalRef = this.modalService.open(
			FileUploadModalComponent,
			{
				size: 'xl',
				centered: true,
				backdrop: 'static',
			},
		);

		// Wait for the modal to initialize, then add the images
		setTimeout(() => {
			uploadModalRef.componentInstance.addImageBlobsToQueue(
				imageBlobs,
				sourceFileName,
				parentArticleId,
			);
		}, 100);

		// Handle modal result if needed
		uploadModalRef.result
			.then(() => {
				// Upload modal closed successfully
				console.log('File upload modal closed');
			})
			.catch(() => {
				// Modal was dismissed
				console.log('File upload modal dismissed');
			});
	}

	/**
	 * Sorts the articles by the specified column
	 */
	sortArticles(column: 'title' | 'dateCreated'): void {
		if (this.sortColumn === column) {
			// Toggle direction if clicking same column
			this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
		} else {
			// Set new column and reset to ascending
			this.sortColumn = column;
			this.sortDirection = 'asc';
		}

		// Always re-run the complete search to apply sorting
		this.filteredArticles = this.searchArticles();
	}

	/**
	 * Applies sorting to the given articles array
	 */
	private applySorting(articles: Article[]): Article[] {
		if (!this.sortColumn) {
			return articles;
		}

		return [...articles].sort((a, b) => {
			let aValue: string | number;
			let bValue: string | number;

			switch (this.sortColumn) {
				case 'title':
					aValue = a.title.toLowerCase();
					bValue = b.title.toLowerCase();
					break;
				case 'dateCreated':
					// dateCreated is a Unix timestamp (seconds), convert to number for comparison
					aValue = Number(a.dateCreated);
					bValue = Number(b.dateCreated);
					break;
				default:
					return 0;
			}

			let comparison = 0;
			if (aValue < bValue) {
				comparison = -1;
			} else if (aValue > bValue) {
				comparison = 1;
			}

			return this.sortDirection === 'asc' ? comparison : -comparison;
		});
	}

	/**
	 * Gets the sort icon class for a column
	 */
	getSortIcon(column: 'title' | 'dateCreated'): string {
		if (this.sortColumn !== column) {
			return 'bi-arrow-down-up'; // Default sort icon
		}
		return this.sortDirection === 'asc'
			? 'bi-sort-alpha-down'
			: 'bi-sort-alpha-up';
	}

	/**
	 * Checks if an article matches the selected tags
	 */
	private checkTagMatch(
		searchTagIds: number[],
		articleTags: number[],
	): boolean {
		// If no tags are selected in search, show all articles
		if (searchTagIds.length === 0) {
			return true;
		}

		// OR mode: article needs to have at least one of the searched tags
		return articleTags.some((tagId) => searchTagIds.includes(tagId));
	}

	/**
	 * Get unique tags with counts from filtered articles
	 * In hierarchy mode, returns available subfolders instead
	 */
	getUniqueTagsWithCounts(): Array<{
		tag: BackendTag;
		count: number;
		disabled?: boolean;
	}> {
		// In hierarchy mode, return available subfolders
		if (this.displayMode === 'hierarchy') {
			const subfolders =
				this.currentFolderPath.length === 0
					? this.getRootFolders()
					: this.getAvailableSubfolders();

			return subfolders.map((folder) => ({
				tag: folder.tag,
				count: folder.count,
				disabled: false,
			}));
		}

		// Normal mode: show all available tags from base filtered articles
		const tagCounts = new Map<number, number>();

		// Both OR and AND modes: Show all available tags from base filtered articles
		// This ensures all tags remain visible regardless of current tag selection
		const baseFilteredArticles = this.getBaseFilteredArticles();
		baseFilteredArticles.forEach((article) => {
			article.tags.forEach((tagId) => {
				tagCounts.set(tagId, (tagCounts.get(tagId) || 0) + 1);
			});
		});

		// Convert to array with tag definitions
		const result: Array<{
			tag: BackendTag;
			count: number;
			disabled?: boolean;
		}> = [];
		tagCounts.forEach((count, tagId) => {
			const tag = this.sourceMaterialService.GetTagById(tagId);
			if (tag) {
				result.push({ tag, count, disabled: false });
			}
		});

		// Sort by count descending, then by name
		result.sort((a, b) => {
			if (b.count !== a.count) {
				return b.count - a.count;
			}
			return a.tag.name.localeCompare(b.tag.name);
		});

		return result;
	}

	/**
	 * Get articles filtered by media type and owner only (not by search tags)
	 */
	private getBaseFilteredArticles(): Article[] {
		// Apply media type filters
		const filteredByType = this.cachedArticles.filter((article) => {
			if (article.type === 'image') return this.showImageMedia;
			if (article.type === 'pdf' || article.type === 'doc')
				return this.showFileMedia;
			if (article.type === 'video') return this.showVideoMedia;
			if (article.type === 'audio') return this.showAudioMedia;
			if (article.type === 'text') return this.showFileMedia;
			return false;
		});

		// Apply owner filter
		return filteredByType.filter((article) => {
			return (
				this.owner == 'any' ||
				(this.owner == 'me' && article.isMine) ||
				(this.owner == 'other' && !article.isMine)
			);
		});
	}

	/**
	 * Handle folder click - add tag to search and switch to grouped view
	 */
	onFolderClick(tagName: string): void {
		this.pushToHistory();

		// Add tag to search if not already present
		if (!this.isTagSelected(tagName)) {
			const currentTags = this.getSearchFieldTags(this.searchText);
			const searchWords = this.searchText
				.split(' ')
				.filter((word) => !word.startsWith('#'));

			const newSearchText = [
				...searchWords,
				...currentTags.map((t) => '#' + t),
				'#' + tagName,
			]
				.join(' ')
				.trim();

			this.searchText = newSearchText;
			this.searchTextChange.emit(this.searchText);
		}

		// Switch to grouped display mode
		this.displayMode = 'grouped';
		this.filteredArticles = this.searchArticles();
	}

	/**
	 * Handle folder toggle - add/remove tag from search, with visual feedback
	 */
	onFolderToggle(tagName: string): void {
		this.pushToHistory();

		if (this.isTagSelected(tagName)) {
			// Tag is selected, remove it (use existing toggle functionality)
			this.onToggleTag(tagName);
		} else {
			// Tag is not selected, add it to search
			const currentTags = this.getSearchFieldTags(this.searchText);
			const searchWords = this.searchText
				.split(' ')
				.filter((word) => !word.startsWith('#'));

			const newSearchText = [
				...searchWords,
				...currentTags.map((t) => '#' + t),
				'#' + tagName,
			]
				.join(' ')
				.trim();

			this.searchText = newSearchText;
			this.searchTextChange.emit(this.searchText);
			this.filteredArticles = this.searchArticles();
		}
	}

	/**
	 * Save current state to navigation history
	 */
	pushToHistory(): void {
		// Don't push duplicate states
		const lastState = this.displayHistory[this.displayHistory.length - 1];
		if (
			!lastState ||
			lastState.displayMode !== this.displayMode ||
			lastState.searchText !== this.searchText
		) {
			this.displayHistory.push({
				displayMode: this.displayMode,
				searchText: this.searchText,
			});

			// Limit history size to prevent memory issues
			if (this.displayHistory.length > 50) {
				this.displayHistory.shift();
			}
		}
	}

	/**
	 * Navigate back to previous state
	 */
	navigateBack(): void {
		if (this.displayHistory.length > 0) {
			const previousState = this.displayHistory.pop()!;
			this.displayMode = previousState.displayMode;
			this.searchText = previousState.searchText;
			this.searchTextChange.emit(this.searchText);
			this.filteredArticles = this.searchArticles();
		}
	}

	/**
	 * Check if navigation back is possible
	 */
	canNavigateBack(): boolean {
		return this.displayHistory.length > 0;
	}

	/**
	 * Handle display mode change
	 */
	onDisplayModeChange(
		newMode: 'list' | 'preview' | 'grouped' | 'folders' | 'hierarchy',
	): void {
		// Check if we're leaving hierarchy mode by looking at the folder path
		// (currentFolderPath is only used in hierarchy mode)
		const wasInHierarchyMode = this.currentFolderPath.length > 0;

		// The displayMode is already set by ngModel binding
		this.displayMode = newMode;

		// Reset folder path when switching to/from hierarchy mode
		if (newMode === 'hierarchy' || wasInHierarchyMode) {
			this.currentFolderPath = [];
		}

		// Re-run search to apply new display mode logic
		this.filteredArticles = this.searchArticles();
	}

	// ============= HIERARCHY MODE METHODS =============

	/**
	 * Go back one level in the hierarchy
	 */
	goBack(): void {
		if (this.currentFolderPath.length > 0) {
			this.currentFolderPath.pop();
			this.filteredArticles = this.searchArticles();
		}
	}

	/**
	 * Get current folder path for hierarchy navigation
	 */
	getCurrentFolderPath(): number[] {
		return [...this.currentFolderPath];
	}

	/**
	 * Get articles that match the current folder path exactly
	 * Root level (empty path): articles with no tags
	 * Folder level: articles where tags match the current path exactly
	 */
	getArticlesAtCurrentPath(): Article[] {
		const baseFilteredArticles = this.getBaseFilteredArticles();

		if (this.currentFolderPath.length === 0) {
			// Root level - show articles with no tags
			return baseFilteredArticles.filter(
				(article) => article.tags.length === 0,
			);
		}

		// Folder level - show articles with exactly the same path (direct articles only)
		return baseFilteredArticles.filter((article) => {
			// Article must have exactly the same number of tags as current path
			if (article.tags.length !== this.currentFolderPath.length) {
				return false;
			}

			// Check if article's tag path matches current path exactly
			return this.currentFolderPath.every(
				(tagId, index) => article.tags[index] === tagId,
			);
		});
	}

	/**
	 * Get articles that are in root (no tags)
	 */
	getRootArticles(): Article[] {
		const baseFilteredArticles = this.getBaseFilteredArticles();
		return baseFilteredArticles.filter(
			(article) => article.tags.length === 0,
		);
	}

	/**
	 * Get available subfolders at current path
	 */
	getAvailableSubfolders(): Array<{ tag: BackendTag; count: number }> {
		const baseFilteredArticles = this.getBaseFilteredArticles();
		const subfolderCounts = new Map<number, number>();
		const targetDepth = this.currentFolderPath.length;

		baseFilteredArticles.forEach((article) => {
			// Article must have more tags than current path to have subfolders
			if (article.tags.length <= targetDepth) {
				return;
			}

			// Check if article's path matches current path up to the current depth
			const pathMatches = this.currentFolderPath.every(
				(tagId, index) => article.tags[index] === tagId,
			);

			if (pathMatches) {
				// The next tag in the sequence is a potential subfolder
				const subfolderTagId = article.tags[targetDepth];
				subfolderCounts.set(
					subfolderTagId,
					(subfolderCounts.get(subfolderTagId) || 0) + 1,
				);
			}
		});

		// Convert to array with tag definitions
		const result: Array<{ tag: BackendTag; count: number }> = [];
		subfolderCounts.forEach((count, tagId) => {
			const tag = this.sourceMaterialService.GetTagById(tagId);
			if (tag) {
				result.push({ tag, count });
			}
		});

		// Sort by name
		result.sort((a, b) => a.tag.name.localeCompare(b.tag.name));

		return result;
	}

	/**
	 * Get root folders (first-level tags from articles)
	 */
	getRootFolders(): Array<{ tag: BackendTag; count: number }> {
		const baseFilteredArticles = this.getBaseFilteredArticles();
		const rootFolderCounts = new Map<number, number>();

		baseFilteredArticles.forEach((article) => {
			// Only consider articles that have at least one tag
			if (article.tags.length > 0) {
				const rootTagId = article.tags[0];
				rootFolderCounts.set(
					rootTagId,
					(rootFolderCounts.get(rootTagId) || 0) + 1,
				);
			}
		});

		// Convert to array with tag definitions
		const result: Array<{ tag: BackendTag; count: number }> = [];
		rootFolderCounts.forEach((count, tagId) => {
			const tag = this.sourceMaterialService.GetTagById(tagId);
			if (tag) {
				result.push({ tag, count });
			}
		});

		// Sort by name
		result.sort((a, b) => a.tag.name.localeCompare(b.tag.name));

		return result;
	}

	/**
	 * Navigate into a folder by adding tag to current path
	 */
	navigateToFolder(tagId: number): void {
		this.currentFolderPath.push(tagId);
		// Refresh the filtered articles to show only articles in this folder
		this.filteredArticles = this.searchArticles();
	}

	/**
	 * Navigate to specific breadcrumb level (0 = root, 1 = first folder, etc.)
	 */
	navigateToBreadcrumb(pathIndex: number): void {
		if (pathIndex === 0) {
			// Navigate to root
			this.currentFolderPath = [];
		} else {
			// Navigate to specific folder level
			this.currentFolderPath = this.currentFolderPath.slice(0, pathIndex);
		}
		// Refresh the filtered articles to show articles at new path
		this.filteredArticles = this.searchArticles();
	}

	/**
	 * Get breadcrumb navigation items for current path
	 */
	getBreadcrumbs(): Array<{ name: string; path: number[]; isLast: boolean }> {
		const breadcrumbs: Array<{
			name: string;
			path: number[];
			isLast: boolean;
		}> = [];

		// Add root breadcrumb
		breadcrumbs.push({
			name: 'Juurikansio',
			path: [],
			isLast: this.currentFolderPath.length === 0,
		});

		// Add folder breadcrumbs
		for (let i = 0; i < this.currentFolderPath.length; i++) {
			const tagId = this.currentFolderPath[i];
			const tag = this.sourceMaterialService.GetTagById(tagId);
			const folderPath = this.currentFolderPath.slice(0, i + 1);

			breadcrumbs.push({
				name: tag ? tag.description || tag.name : `Tag ${tagId}`,
				path: folderPath,
				isLast: i === this.currentFolderPath.length - 1,
			});
		}

		return breadcrumbs;
	}
}

/**
 * Modal component for warning about export conflicts when deleting articles
 */
@Component({
	selector: 'app-export-conflict-modal',
	standalone: true,
	imports: [CommonModule],
	template: `
		<div class="modal-header">
			<h4 class="modal-title">
				<i
					class="bi bi-exclamation-triangle-fill text-warning me-2"
				></i>
				Tiedosto on käytössä
			</h4>
		</div>

		<div class="modal-body">
			<div class="alert alert-danger d-flex align-items-start">
				<i class="bi bi-info-circle-fill me-2 mt-1"></i>
				<div>
					<strong>Varoitus:</strong> "{{ articleTitle }}" on käytössä
					seuraavissa peleissä:
				</div>
			</div>

			<ul class="list-group list-group-flush mb-3">
				<li
					class="list-group-item"
					*ngFor="let exportName of exportNames"
				>
					• {{ exportName }}
				</li>
			</ul>

			<div
				*ngIf="childArticleCount > 0"
				class="alert alert-danger d-flex align-items-start mb-3"
			>
				<i class="bi bi-info-circle-fill me-2 mt-1"></i>
				<div>
					<strong>Varoitus:</strong> Tämän artikkelin mukana
					poistetaan myös {{ childArticleCount }} alitiedosto{{
						childArticleCount > 1 ? 'a' : ''
					}}.
				</div>
			</div>

			<p class="mb-0">
				Jos poistat tämän tiedoston, sitä ei voida enää käyttää näissä
				peleissä. Haluatko varmasti jatkaa?
			</p>
		</div>

		<div class="modal-footer">
			<button type="button" class="btn btn-secondary" (click)="cancel()">
				<i class="bi bi-x-lg me-1"></i>
				Peruuta
			</button>
			<button type="button" class="btn btn-danger" (click)="confirm()">
				<i class="bi bi-trash me-1"></i>
				Poista
			</button>
		</div>
	`,
	styles: [
		`
			.alert {
				font-size: 0.9rem;
			}

			.modal-footer .btn {
				min-width: 120px;
			}
		`,
	],
})
export class ExportConflictModalComponent {
	@Input() exportNames: string[] = [];
	@Input() articleTitle = '';
	@Input() childArticleCount = 0;

	constructor(public activeModal: NgbActiveModal) {}

	confirm(): void {
		this.activeModal.close(true);
	}

	cancel(): void {
		this.activeModal.close(false);
	}
}
